import { useState } from 'react';
import Collapsible from 'react-collapsible';
import { BsCaretDownFill, BsCaretUpFill } from 'react-icons/bs';
import styled from 'styled-components';

import { DetailArrayValue } from 'components/DetailsView/DetailArrayValue';
import { DetailValue } from 'components/DetailsView/DetailValue';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { TotemGallery } from 'components/TotemGallery';
import { TaskState } from 'data/__generated__';
import { formatDurationMinutes } from 'helpers/dateTimes';

import { TASK_STATES_CLOSED, formatWindow, stopAddress, stopWindow, taskLabel, type TourTask } from '../helpers';
import { WindowChip } from '../components/StopStatus';
import { TaskStatusTag } from '../components/TourStatusTag';
import { Caret, DeliveryHeader, Details, DetailsColumn, Empty, Label, Title } from './DeliveryCard';
import { formatHour } from './helpers';

/** One task of the tour, open by default like a delivery: the page is read to control the whole tour in one pass. */
export const TaskCard = ({ task, sequence }: { task: TourTask; sequence: number }) => {
    const [isOpen, setIsOpen] = useState(true);
    const { start, stop } = stopWindow(task);

    return (
        <Collapsible
            transitionTime={150}
            overflowWhenOpen="visible"
            open={isOpen}
            trigger={
                <DeliveryHeader onClick={() => setIsOpen((open) => !open)} data-test="tour-details-task">
                    <Caret>{isOpen ? <BsCaretUpFill size={13} /> : <BsCaretDownFill size={13} />}</Caret>
                    <Title>
                        {sequence}. {taskLabel(task)}
                    </Title>
                    <WindowChip $muted={!start && !stop}>{formatWindow(start, stop)}</WindowChip>
                    <TaskStatusTag state={task.state} />
                </DeliveryHeader>
            }
        >
            <TaskDetails task={task} />
        </Collapsible>
    );
};

export const TaskDetails = ({ task }: { task: TourTask }) => {
    const arrivedAt = task.stateHistory.find(({ state }) => state === TaskState.InProgress)?.createdAt ?? null;
    const closure = TASK_STATES_CLOSED.includes(task.state) ? task.stateHistory[task.stateHistory.length - 1] : null;
    const onSiteMinutes =
        arrivedAt && closure && closure.state !== TaskState.Aborted
            ? Math.round((closure.createdAt - arrivedAt) / 60_000)
            : null;
    const instructions = task.instructions.map(({ label, isDone }) => `${isDone ? '✓' : closure ? '✗' : '•'} ${label}`);

    return (
        <Details>
            <DetailsColumn>
                <DetailValue label="Site" value={[task.site?.name, stopAddress(task)].filter(Boolean).join(' · ')} />
                <DetailValue label="Description" value={task.description} />
                {instructions.length > 0 && <DetailArrayValue label="Instructions" values={instructions} />}
                <DetailValue
                    label="Temps sur place"
                    sublabel={`estimé ${formatDurationMinutes(task.theoricServiceTime)}`}
                    value={onSiteMinutes != null ? formatDurationMinutes(onSiteMinutes) : '—'}
                />
                <DetailValue
                    label="Arrivée → clôture"
                    value={`${formatHour(arrivedAt)} → ${formatHour(closure?.createdAt)}`}
                />
                {task.driversComment ? (
                    <DetailValue label="Commentaire de clôture" value={task.driversComment} />
                ) : null}
                <StateHistory
                    label="Étapes de la tâche"
                    records={task.stateHistory.map((record) => ({
                        ...record,
                        state: <TaskStatusTag state={record.state} />,
                    }))}
                />
            </DetailsColumn>
            <DetailsColumn>
                <Label>Photos avant / après</Label>
                {task.instructions.map(({ _id, label, controlPhotos }) => (
                    <div key={_id}>
                        <InstructionLabel>{label}</InstructionLabel>
                        {controlPhotos.length ? <TotemGallery images={controlPhotos} /> : <Empty>Aucune photo</Empty>}
                    </div>
                ))}
            </DetailsColumn>
        </Details>
    );
};

const InstructionLabel = styled.div`
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 4px;
`;
